/**
 * MonthLedgerRow — one campaign summary row in the reconciliation table that
 * expands into a per-billing-month ledger.
 *
 * Invoices are issued monthly, so the verdict is reconciled per month and rolled
 * up here. The summary row shows the rolled-up impressions / billed / invoiced /
 * $-at-risk; the expanded detail shows each month with its planned / impressions /
 * billed / invoiced / paid status, a maturity pill, and any per-month checks.
 * PENDING (not-yet-due) months are muted and show "—" at risk so a mid-flight
 * campaign never reads as under-billed. Impressions and the tracker Paid status
 * are DISPLAY-ONLY — neither drives a check or $ at risk.
 *
 * Exports the shared presentational helpers (fmtUsd / SEV_COLOR / SeverityPill) so
 * the dashboard renders a single, identical set.
 */

import React from 'react';
import { StatusPill } from '../ui';
import { fmtDateRange } from '../Invoicing/dateFormat';
import { paidVariant } from '../Invoicing/statusText';
import {
  ReconciliationFinding,
  ReconciliationSeverity,
  MonthlyReconciliationRow,
  CHECK_LABEL,
} from './types';
import './Reconciliation.css';

export const fmtUsd = (n?: number): string =>
  typeof n === 'number' && Number.isFinite(n)
    ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : '—';

/** Integer with thousands separators, or "—". */
export const fmtInt = (n?: number): string =>
  typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString('en-US') : '—';

/** "delivered / contracted (nn%)" impressions cell — either side may be missing. */
export const fmtImps = (delivered?: number, contracted?: number): string => {
  if (delivered === undefined && contracted === undefined) return '—';
  const pct =
    typeof delivered === 'number' && typeof contracted === 'number' && contracted > 0
      ? ` (${Math.round((delivered / contracted) * 100)}%)`
      : '';
  return `${fmtInt(delivered)} / ${fmtInt(contracted)}${pct}`;
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
/** '2026-05' → "May 2026" (mirrors the backend's monthKeyLabel). */
const monthKeyLabel = (mk: string): string => {
  const m = mk.match(/^(\d{4})-(\d{2})$/);
  const name = m ? MONTH_NAMES[Number(m[2]) - 1] : undefined;
  return m && name ? `${name} ${m[1]}` : mk;
};

const IMPS_CAVEAT =
  'Delivered vs contracted impressions — informational, ad-server only (YouTube partner-sold ' +
  'delivery is not attributable per order); never part of $ at risk.';

const MATCH_QUALITY_LABEL: Record<'order' | 'fuzzy' | 'none', string> = {
  order: 'matched by order #',
  fuzzy: 'matched by advertiser + dates',
  none: 'no invoice PDF matched',
};

export const SEV_COLOR: Record<ReconciliationSeverity, string> = {
  critical: 'var(--danger)',
  warning: 'var(--warning)',
  ok: 'var(--success)',
};

export const SeverityPill: React.FC<{ s: ReconciliationSeverity }> = ({ s }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 9px',
      borderRadius: 'var(--radius-pill)',
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: '#fff',
      background: SEV_COLOR[s],
    }}
  >
    {s}
  </span>
);

/** Distinct flagged-check labels across the campaign + all of its months. */
function flaggedCheckLabels(f: ReconciliationFinding): string {
  const all = [...f.checks, ...(f.months ?? []).flatMap((m) => m.checks)].filter((c) => c.flagged);
  return Array.from(new Set(all.map((c) => CHECK_LABEL[c.key]))).join(', ');
}

/** Tracker payment status — display-only ("show, don't flag"). */
const PaidCell: React.FC<{ status?: string }> = ({ status }) => {
  const v = paidVariant(status);
  if (v === 'none') return <span style={{ color: 'var(--fg-4)' }}>—</span>;
  if (v === 'paid') {
    return (
      <span title={`Tracker status: ${status}`}>
        <StatusPill variant="ok" size="sm">Paid</StatusPill>
      </span>
    );
  }
  if (v === 'unpaid') {
    return (
      <span title={`Tracker status: ${status}`}>
        <StatusPill variant="pending" size="sm">Unpaid</StatusPill>
      </span>
    );
  }
  return (
    <span className="rec-mo-status-text" title={`Tracker status: ${status}`}>
      {status}
    </span>
  );
};

const MonthStatusCell: React.FC<{ m: MonthlyReconciliationRow; scanned: boolean }> = ({ m, scanned }) => {
  if (m.maturity === 'pending') {
    return (
      <span title="This month isn't due yet — excluded from $ at risk">
        <StatusPill variant="pending" size="sm">Not yet due</StatusPill>
      </span>
    );
  }
  if (m.checks.some((c) => c.flagged)) {
    return <SeverityPill s={m.worstSeverity} />;
  }
  if (!scanned) {
    // Cached run: the invoice-PDF cross-check didn't run, so "invoiced as planned"
    // would overclaim — only the billing (tracker) side was verified.
    return (
      <span title="Billed as planned — the invoice-PDF cross-check didn't run on this cached run. Re-run with Refresh on to verify invoicing.">
        <StatusPill variant="draft" size="sm">Billed as planned</StatusPill>
      </span>
    );
  }
  return (
    <span title="Billed and invoiced as planned">
      <StatusPill variant="ok" size="sm">Reconciled</StatusPill>
    </span>
  );
};

const MonthRow: React.FC<{ m: MonthlyReconciliationRow; scanned: boolean }> = ({ m, scanned }) => {
  const detail = m.checks.filter((c) => c.flagged).map((c) => c.detail).join(' ');
  const impsTitle = `${IMPS_CAVEAT}${m.deliveredValue !== undefined ? ` Delivered ≈ ${fmtUsd(m.deliveredValue)}.` : ''}`;
  const invoicedTitle = [
    m.invoiceNumbers?.length ? `Invoice #: ${m.invoiceNumbers.join(', ')}` : undefined,
    scanned && m.matchQuality ? `Invoice PDFs ${MATCH_QUALITY_LABEL[m.matchQuality]}` : undefined,
  ]
    .filter(Boolean)
    .join(' — ');
  return (
    <div className={`rec-mo-row${m.maturity === 'pending' ? ' rec-mo-muted' : ''}`}>
      <div style={{ fontWeight: 600 }}>{monthKeyLabel(m.monthKey)}</div>
      <div style={{ color: 'var(--fg-3)' }}>{fmtDateRange(m.monthlyFlightStart, m.monthlyFlightEnd)}</div>
      <div className="rec-mo-num">{fmtUsd(m.planned)}</div>
      <div className="rec-mo-delivered" title={impsTitle}>{fmtImps(m.deliveredImpressions, m.contractedImpressions)}</div>
      <div className="rec-mo-num">{fmtUsd(m.billed)}</div>
      <div className="rec-mo-num" title={invoicedTitle || undefined}>
        {fmtUsd(m.invoiced)}
      </div>
      <div><PaidCell status={m.status} /></div>
      <div><MonthStatusCell m={m} scanned={scanned} /></div>
      <div className="rec-mo-detail">{detail || (m.maturity === 'pending' ? 'Not billed yet — invoice not due.' : '—')}</div>
      <div className="rec-mo-num" style={m.dollarsAtRisk > 0 ? { color: SEV_COLOR[m.worstSeverity], fontWeight: 700 } : undefined}>
        {m.maturity === 'pending' || m.dollarsAtRisk <= 0 ? '—' : fmtUsd(m.dollarsAtRisk)}
      </div>
    </div>
  );
};

interface Props {
  finding: ReconciliationFinding;
  expanded: boolean;
  onToggle: () => void;
  colCount: number;
  hidePending: boolean;
  /** From report.scannedInvoicePdfs — false on cached runs (undefined on old reports = assume scanned). */
  scannedInvoicePdfs?: boolean;
}

const MonthLedgerRow: React.FC<Props> = ({ finding: f, expanded, onToggle, colCount, hidePending, scannedInvoicePdfs }) => {
  const months = f.months ?? [];
  const expandable = months.length > 0;
  const visibleMonths = hidePending ? months.filter((m) => m.maturity === 'mature') : months;
  const hiddenPending = months.length - visibleMonths.length;
  const scanned = scannedInvoicePdfs !== false;

  const onKeyDown = (e: React.KeyboardEvent) => {
    // Only toggle from the row itself, not from a click on the inner advertiser link.
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (expandable) onToggle();
    }
  };

  return (
    <>
      <tr
        className={`rec-row-summary${expandable ? ' rec-clickable' : ''}`}
        onClick={expandable ? onToggle : undefined}
        onKeyDown={onKeyDown}
        role={expandable ? 'button' : undefined}
        tabIndex={expandable ? 0 : undefined}
        aria-expanded={expandable ? expanded : undefined}
      >
        <td style={styles.td}>
          {expandable && <span className="rec-chevron">{expanded ? '▾' : '▸'}</span>}
        </td>
        <td style={styles.td}><SeverityPill s={f.worstSeverity} /></td>
        <td style={styles.td}>
          {f.ioLink ? (
            <a href={f.ioLink} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }} onClick={(e) => e.stopPropagation()}>
              {f.advertiser}
            </a>
          ) : (
            f.advertiser
          )}
          {f.agency && <span style={{ color: 'var(--fg-4)', fontSize: 11 }}> · {f.agency}</span>}
        </td>
        <td style={styles.td}>{f.orderNumber ?? f.campaignOrderId ?? '—'}</td>
        <td style={styles.td}>
          {f.source === 'io-only'
            ? f.flightEnd ?? '—'
            : f.source === 'invoice-only'
            ? f.monthKeys?.join(', ') || '—'
            : fmtDateRange(f.flightStart, f.flightEnd)}
        </td>
        <td style={styles.td}>
          {f.source === 'io-only' ? (
            <span style={{ color: 'var(--fg-4)' }}>IO (no invoice)</span>
          ) : f.source === 'invoice-only' ? (
            <span style={{ color: 'var(--fg-4)' }}>Invoice (no campaign)</span>
          ) : f.complete ? (
            <StatusPill variant="ok" size="sm">Complete</StatusPill>
          ) : f.pendingMonths ? (
            <span title={`${f.pendingMonths} month(s) not yet due — excluded from $ at risk`}>
              <StatusPill variant="pending" size="sm">{f.pendingMonths} pending</StatusPill>
            </span>
          ) : (
            <span style={{ color: 'var(--fg-4)' }}>—</span>
          )}
        </td>
        <td style={{ ...styles.td, ...styles.num, color: 'var(--fg-3)' }} title={IMPS_CAVEAT}>
          {fmtImps(f.deliveredImpressions, f.contractedImpressions)}
        </td>
        <td style={{ ...styles.td, ...styles.num }}>{fmtUsd(f.billedValue)}</td>
        <td
          style={{ ...styles.td, ...styles.num }}
          title={f.invoiceNumbers?.length ? `Invoice #: ${f.invoiceNumbers.join(', ')}` : undefined}
        >
          {fmtUsd(f.invoicedValue)}
        </td>
        <td style={{ ...styles.td, ...styles.num, fontWeight: 700, color: SEV_COLOR[f.worstSeverity] }}>
          {fmtUsd(f.totalDollarsAtRisk)}
        </td>
        <td style={{ ...styles.td, fontSize: 12, color: 'var(--fg-3)' }}>{flaggedCheckLabels(f) || '—'}</td>
      </tr>

      {expandable && expanded && (
        <tr>
          <td className="rec-mo-detail-cell" colSpan={colCount}>
            <div className="rec-mo-list">
              <div className="rec-mo-row rec-mo-head">
                <div>Month</div>
                <div>Flight</div>
                <div className="rec-mo-num">Planned</div>
                <div className="rec-mo-num" title={IMPS_CAVEAT}>Impressions</div>
                <div className="rec-mo-num">Billed</div>
                <div className="rec-mo-num">Invoiced</div>
                <div>Paid</div>
                <div>Status</div>
                <div>Detail</div>
                <div className="rec-mo-num">$ at risk</div>
              </div>
              {visibleMonths.map((m) => (
                <MonthRow key={m.monthKey} m={m} scanned={scanned} />
              ))}
              {hiddenPending > 0 && (
                <div className="rec-mo-row" style={{ gridTemplateColumns: '1fr', color: 'var(--fg-4)', fontSize: 11.5 }}>
                  {hiddenPending} not-yet-due month{hiddenPending > 1 ? 's' : ''} hidden.
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  td: { padding: 12, verticalAlign: 'top', color: 'var(--fg-1)' },
  num: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
};

export default MonthLedgerRow;
